/**
 * Phone number validation and formatting utility
 * Single source of truth for phone number operations
 */
export class PhoneUtil {
  /**
   * Validate and format phone number to E.164 format
   * @param phoneNumber - Raw phone number input
   * @returns Formatted phone number with + prefix, or null if invalid
   */
  static validateAndFormat(phoneNumber: string): string | null {
    if (!phoneNumber) {
      return null;
    }

    // Remove all non-digit characters
    const cleanNumber = phoneNumber.replace(/\D/g, '');

    // If already in E.164 format (starts with +)
    if (phoneNumber.startsWith('+')) {
      return cleanNumber.length >= 10 ? '+' + cleanNumber : null;
    }

    // US/Canada number handling (10 digits)
    if (cleanNumber.length === 10) {
      return '+1' + cleanNumber;
    }

    // US/Canada with country code (11 digits starting with 1)
    if (cleanNumber.length === 11 && cleanNumber.startsWith('1')) {
      return '+' + cleanNumber;
    }

    // International numbers (10+ digits)
    if (cleanNumber.length >= 10) {
      return '+' + cleanNumber;
    }

    // Invalid - too short
    return null;
  }

  /**
   * Format phone number for display
   * @param phoneNumber - Phone number in E.164 format
   * @returns Formatted display string
   */
  static formatForDisplay(phoneNumber: string): string {
    if (!phoneNumber) {
      return '';
    }

    const cleanNumber = phoneNumber.replace(/\D/g, '');

    // US/Canada format: +1 (XXX) XXX-XXXX
    if (cleanNumber.startsWith('1') && cleanNumber.length === 11) {
      return `+1 (${cleanNumber.substr(1, 3)}) ${cleanNumber.substr(4, 3)}-${cleanNumber.substr(7, 4)}`;
    }

    // International: +XX XXX XXX XXXX
    if (cleanNumber.length >= 10) {
      const countryCode = cleanNumber.substr(0, cleanNumber.length - 10);
      const remaining = cleanNumber.substr(countryCode.length);
      return `+${countryCode} ${remaining.substr(0, 3)} ${remaining.substr(3, 3)} ${remaining.substr(6)}`;
    }

    // Fallback: return with + prefix
    return '+' + cleanNumber;
  }

  /**
   * Check if phone number is valid
   * @param phoneNumber - Phone number to validate
   * @returns true if valid, false otherwise
   */
  static isValid(phoneNumber: string): boolean {
    return this.validateAndFormat(phoneNumber) !== null;
  }
}
